"use client";

import { useMemo } from "react";

import type { BuilderContent } from "@builder.io/sdk-react";
import Image from "next/image";
import { usePathname } from "next/navigation";

import { useConfig } from "@jti/app-context/useConfig";
import { Header as UIHeader } from "@jti/ui/eva-components/Header";
import { Header as MobileFirstUIHeader } from "@jti/ui/eva-components/MobileFirstHeader";

import { useLocale } from "@/context/LocaleContext";
import { usePageConfig } from "@/context/PageConfigContext";
import { useLanguages } from "@/hooks/useLanguages";
import { usePathnameWithoutLocale } from "@/hooks/usePathnameWithoutLocale";
import { Content } from "@/libs/builder/Content";
import type { ReferenceObject } from "@/libs/builder/tools/isReferenceObjectWithValue";
import { getPathnameWithoutLocale } from "@/utils/getPathnameWithoutLocale";

import { CartMini } from "../CartMini/CartMini";

import type { HeaderProps } from "./inputs/headerInputs.builder";

import { useCartMini } from "./useCartMini";
import { navLinksSecondaryNavigationAdapter } from "./utils";

import styles from "./Header.module.scss";

export function Header(props: HeaderProps) {
  const {
    logo,
    navLinks = [],
    tools: {
      accountLink,
      accountLabel,
      accountLoadingLabel,
      mobileAppLink,
      storeLocatorLink,
      storeLocatorLabel,
      openMenuLabel,
      closeMenuLabel,
      navAriaLabel,
      languageLabel,
      searchLabel,
      resultsLink,
      cartLabel,
      expandBreadcrumbsLabel,
      loyaltyPointsCurrency,
    } = {},
    products,
    accountDropdownSettings: rawAccountDropdownSettings,
    enableUserIndication,
    enableUserFullNameForIndication,
  } = props;

  const accountDropdownSettings = useMemo(() => {
    if (!rawAccountDropdownSettings) return undefined;
    const { tierTranslations: tierList, ...rest } = rawAccountDropdownSettings;
    const tierTranslations = tierList?.reduce<Record<string, string>>((acc, { tier, label }) => {
      acc[tier.toLowerCase()] = label;
      return acc;
    }, {});
    return { ...rest, tierTranslations };
  }, [rawAccountDropdownSettings]);

  const {
    darkMode,
    hideNavigation,
    simplifiedHeader,
    hideBreadcrumbs,
    hideBreadcrumbsOnMobile,
    breadcrumbsList,
    overlayHeader,
    useHeadingTagForBreadcrumbs,
    navigationThemeOverride,
    transparentHeaderTheme,
  } = usePageConfig();

  const { DISABLE_COMMERCE, languageSelectorVariant, isMobileFirstNavigationEnabled } = useConfig();
  const languages = useLanguages();
  const { cartItemsCount, openCart } = useCartMini();

  const { currentLocale } = useLocale();
  const pathnameWithoutLocale = usePathnameWithoutLocale();
  const pathname = usePathname();
  const breadcrumbs = hideBreadcrumbs ? [] : breadcrumbsList;

  const renderLogoImage = ({ src, alt }: { src: string; alt: string }) => (
    <Image src={src} alt={alt} layout="fill" priority className={styles.logo} />
  );

  const validatedNavLinks = useMemo(
    () => navLinksSecondaryNavigationAdapter(navLinks, pathnameWithoutLocale, currentLocale),
    [navLinks, pathnameWithoutLocale, currentLocale]
  );

  const secondaryNavigationList = useMemo(() => {
    return validatedNavLinks.reduce<ReferenceObject<BuilderContent>[]>((acc, navLink) => {
      if ("secondaryNavigation" in navLink) {
        acc.push(navLink.secondaryNavigation);
      }
      return acc;
    }, []);
  }, [validatedNavLinks]);

  function onLogout() {
    const logoutRedirectPage = accountDropdownSettings?.logoutRedirectPage;
    if (logoutRedirectPage && pathnameWithoutLocale !== getPathnameWithoutLocale(logoutRedirectPage, currentLocale)) {
      window.location.href = logoutRedirectPage;
    } else {
      window.location.reload();
    }
  }

  const onOpenCart = () => {
    if (!DISABLE_COMMERCE) {
      openCart();
    }
  };

  const HeaderComponent = isMobileFirstNavigationEnabled ? MobileFirstUIHeader : UIHeader;

  return (
    <HeaderComponent
      isDarkMode={navigationThemeOverride || darkMode}
      transparentHeaderTheme={transparentHeaderTheme}
      logo={{
        ...logo,
        renderImage: renderLogoImage,
      }}
      hideNavigation={hideNavigation}
      isSimplified={simplifiedHeader}
      navLinks={validatedNavLinks}
      accountLink={accountLink}
      accountLabel={accountLabel}
      accountLoadingLabel={accountLoadingLabel}
      accountDropdownSettings={accountDropdownSettings}
      renderMiniCart={() => <CartMini shopButtonLabel={accountDropdownSettings?.shopButtonLabel} />}
      onLogOut={onLogout}
      mobileAppLink={mobileAppLink}
      currentPathname={pathname ?? undefined}
      storeLocatorLink={storeLocatorLink}
      storeLocatorLabel={storeLocatorLabel}
      openMenuLabel={openMenuLabel}
      closeMenuLabel={closeMenuLabel}
      navAriaLabel={navAriaLabel}
      languageLabel={languageLabel}
      searchLabel={searchLabel}
      isEnabledUserIndication={enableUserIndication}
      isEnabledUserNameForIndication={enableUserFullNameForIndication}
      breadcrumbsList={breadcrumbs}
      hideBreadcrumbsOnMobile={hideBreadcrumbsOnMobile}
      expandBreadcrumbsLabel={expandBreadcrumbsLabel}
      languages={languages}
      useHeadingTagForBreadcrumbs={useHeadingTagForBreadcrumbs}
      resultsLink={resultsLink}
      withCart={!DISABLE_COMMERCE}
      cartLabel={cartLabel}
      cartItemsCount={cartItemsCount}
      onCartOpen={onOpenCart}
      loyaltyPointsCurrency={loyaltyPointsCurrency}
      overlayHeader={overlayHeader}
      variant={languageSelectorVariant?.header?.variant}
      renderSubmenu={(secondaryNavigationId) => {
        const content = secondaryNavigationList.find(({ id }) => secondaryNavigationId === id)?.value;

        return (
          <Content
            model="secondary-navigation"
            content={content}
            data={{
              products,
            }}
          />
        );
      }}
    />
  );
}
